import type { LocationHours, StorefrontLocation } from '@/features/storefront/api/locations';

/**
 * The arithmetic behind the store locator: "open now", directions, the map
 * and the search box. Pure, so the section and its tests ask the same thing.
 */

const DEFAULT_ZONE = 'Asia/Kolkata';

const WEEKDAY_INDEX: Record<string, number> = {
  Mon: 0,
  Tue: 1,
  Wed: 2,
  Thu: 3,
  Fri: 4,
  Sat: 5,
  Sun: 6,
};

/** `"09:30"` as minutes past midnight, or null for anything else. */
export function clockMinutes(value: string): number | null {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(value.trim());
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
}

function formatterFor(zone: string): Intl.DateTimeFormat {
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  };
  try {
    return new Intl.DateTimeFormat('en-US', { ...options, timeZone: zone });
  } catch {
    // An unknown zone name reads as India's, which is what the server does.
    return new Intl.DateTimeFormat('en-US', { ...options, timeZone: DEFAULT_ZONE });
  }
}

/** The weekday (0 = Monday) and minutes past midnight of `moment` in `zone`. */
export function wallClock(moment: Date, zone: string): { weekday: number; minutes: number } {
  const parts = formatterFor(zone).formatToParts(moment);
  const part = (type: string) => parts.find((each) => each.type === type)?.value ?? '';
  const hour = Number(part('hour')) % 24;
  return { weekday: WEEKDAY_INDEX[part('weekday')] ?? 0, minutes: hour * 60 + Number(part('minute')) };
}

/**
 * Whether a location is open at `moment`, read on the store's clock.
 *
 * `null` when the hours are not listed - no badge rather than a "closed" the
 * shop never said. A weekday missing from a listed week is closed. Closing
 * time is exclusive. Mirrors `is_open_at` in `services/store/locations.py`.
 */
export function isOpenNow(hours: LocationHours[], moment: Date, zone: string): boolean | null {
  if (hours.length === 0) return null;
  const { weekday, minutes } = wallClock(moment, zone || DEFAULT_ZONE);
  const today = hours.find((row) => row.weekday === weekday);
  if (!today || today.closed) return false;
  const opens = clockMinutes(today.opens);
  const closes = clockMinutes(today.closes);
  if (opens === null || closes === null) return false;
  return minutes >= opens && minutes < closes;
}

/** Both halves of a pin, or null - half a coordinate is no place at all. */
export function coordinatesOf(location: StorefrontLocation): { lat: string; lng: string } | null {
  if (!location.latitude || !location.longitude) return null;
  return { lat: location.latitude, lng: location.longitude };
}

/** One line of address, as a shopper would read it out. */
export function addressLine(location: StorefrontLocation): string {
  return [
    location.address_line1,
    location.address_line2,
    location.city,
    [location.state, location.pincode].filter(Boolean).join(' '),
  ]
    .map((part) => (part ?? '').trim())
    .filter(Boolean)
    .join(', ');
}

/**
 * A Google Maps directions link - a plain URL, no SDK and no key. The pin
 * when the merchant gave one, else the address, which Maps geocodes itself.
 */
export function directionsUrl(location: StorefrontLocation): string {
  const pin = coordinatesOf(location);
  const destination = pin ? `${pin.lat},${pin.lng}` : addressLine(location);
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
}

/** How far the map reaches around the pin, in degrees: a few streets. */
const MAP_SPAN = 0.01;

/** An OpenStreetMap embed centred on the pin, or null without coordinates. */
export function osmEmbedUrl(location: StorefrontLocation): string | null {
  const pin = coordinatesOf(location);
  if (!pin) return null;
  const lat = Number(pin.lat);
  const lng = Number(pin.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  const bbox = [lng - MAP_SPAN, lat - MAP_SPAN, lng + MAP_SPAN, lat + MAP_SPAN]
    .map((value) => value.toFixed(6))
    .join(',');
  return `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(bbox)}&layer=mapnik&marker=${encodeURIComponent(`${pin.lat},${pin.lng}`)}`;
}

/** The search box: a city or a PIN code, case- and space-insensitive. */
export function matchesSearch(location: StorefrontLocation, query: string): boolean {
  const needle = query.trim().toLowerCase().replace(/\s+/g, '');
  if (!needle) return true;
  const haystack = [location.city, location.pincode ?? '', location.name, location.state ?? '']
    .map((value) => value.toLowerCase().replace(/\s+/g, ''));
  return haystack.some((value) => value.includes(needle));
}

/** A `tel:` link from a written number: digits and a leading plus only. */
export function telHref(phone: string): string {
  const trimmed = phone.trim();
  return `tel:${trimmed.startsWith('+') ? '+' : ''}${trimmed.replace(/\D/g, '')}`;
}

/** The weekday's short name in the shopper's language, Monday as 0. */
export function weekdayName(weekday: number, locale: string): string {
  // 2024-01-01 was a Monday; noon UTC keeps every zone on the same date.
  const date = new Date(Date.UTC(2024, 0, 1 + weekday, 12));
  try {
    return new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' }).format(date);
  } catch {
    return new Intl.DateTimeFormat('en', { weekday: 'short', timeZone: 'UTC' }).format(date);
  }
}
